import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, RefreshCw, Copy, CheckCircle, 
  XCircle, Key, AlertCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmployeeManagementPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'onboarding_specialist'
  });
  const [error, setError] = useState('');

  const adminKey = sessionStorage.getItem('adminKey');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ops/employees`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const createEmployee = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/ops/employees`, {
        method: 'POST',
        headers: {
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok) {
        setTempPassword(data.temporary_password);
        setShowCreateModal(false);
        setShowPasswordModal(true);
        setFormData({ name: '', email: '', role: 'onboarding_specialist' });
        fetchEmployees();
      } else {
        setError(data.detail || 'Failed to create employee');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleStatus = async (employee) => {
    const newStatus = employee.status === 'active' ? 'suspended' : 'active';
    
    if (!window.confirm(`Are you sure you want to ${newStatus === 'suspended' ? 'suspend' : 'activate'} ${employee.name}?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops/employees/${employee.id}`, {
        method: 'PATCH',
        headers: {
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        fetchEmployees();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update employee');
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const resetPassword = async (employee) => {
    if (!window.confirm(`Reset password for ${employee.name}? They will be logged out.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/ops/employees/${employee.id}/reset-password`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey }
      });

      if (res.ok) {
        const data = await res.json();
        setTempPassword(data.temporary_password);
        setShowPasswordModal(true);
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to reset password');
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const opsLoginUrl = `${window.location.origin}/ops/login`;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600">Manage operations team access</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchEmployees}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Employee
          </button>
        </div>
      </div>

      {/* Employee Login URL Box */}
      <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
        <div>
          <p className="font-medium text-emerald-800">Employee Login Portal</p>
          <p className="text-sm text-emerald-600">Share this URL with employees to access the Operations dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <code className="px-3 py-2 bg-white rounded-lg text-sm font-mono text-gray-700 border border-emerald-200">
            {opsLoginUrl}
          </code>
          <button
            onClick={() => copyToClipboard(opsLoginUrl)}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy
          </button>
          <a
            href="/ops/login"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 bg-white border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50"
          >
            Open →
          </a>
        </div>
      </div>

      {/* Employees Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : employees.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No employees yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              Add your first employee
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {employees.map(emp => (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{emp.name}</p>
                      <p className="text-sm text-gray-500">{emp.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700 capitalize">{emp.role?.replace('_', ' ')}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      emp.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {emp.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {emp.last_login ? new Date(emp.last_login).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => resetPassword(emp)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleStatus(emp)}
                        className={`p-2 rounded ${
                          emp.status === 'active' 
                            ? 'text-gray-500 hover:text-red-600 hover:bg-red-50' 
                            : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                        }`}
                        title={emp.status === 'active' ? 'Suspend' : 'Activate'}
                      >
                        {emp.status === 'active' ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Employee Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add Employee</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <form onSubmit={createEmployee} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="onboarding_specialist">Onboarding Specialist</option>
                  <option value="qa_analyst">QA Analyst</option>
                  <option value="support">Support</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Temporary Password</h2>
              <p className="text-gray-500 text-sm mt-1">Share this password securely with the employee</p>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-between mb-4">
              <code className="font-mono text-lg">{tempPassword}</code>
              <button
                onClick={() => copyToClipboard(tempPassword)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>

            <p className="text-amber-600 text-sm mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              This password will not be shown again
            </p>

            <button
              onClick={() => {
                setShowPasswordModal(false);
                setTempPassword('');
              }}
              className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
